import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { AutoSizer, Table, Column } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { BsCheckCircleFill, BsClockFill, BsCreditCardFill } from 'react-icons/bs';
import { Spinner } from 'react-bootstrap';
import { formatDate, formatCurrencyFull, formatHours, filterDetailRows, TABLE_ROW_HEIGHT } from '../utils/costingHelpers';

const ALL_COLUMNS = [
    { label: 'Job Date', dataKey: 'jobDate', width: 85, minWidth: 85, rendererType: 'date' },
    { label: 'Day', dataKey: 'jobDay', width: 80, minWidth: 80, rendererType: 'text' },
    { label: 'Site', dataKey: 'site_Name', width: 150, minWidth: 120, rendererType: 'text', wrap: true },
    { label: 'Staff Name', dataKey: 'staffName', width: 220, minWidth: 120, rendererType: 'text', wrap: true },
    { label: 'Role', dataKey: 'roleName', width: 80, minWidth: 80, rendererType: 'text' },
    { label: 'Hours', dataKey: 'hrs', width: 80, minWidth: 80, rendererType: 'hours' },
    { label: 'Nett', dataKey: 'nett', width: 80, minWidth: 80, rendererType: 'currency' },
    { label: 'Reference', dataKey: 'reference', width: 350, minWidth: 180, rendererType: 'text', wrap: true },
    { label: 'Status', dataKey: 'stat', width: 60, minWidth: 60, rendererType: 'status' },
];

const STATUS_CONFIG = {
    'billing': { icon: BsCheckCircleFill, colour: '#28a745', label: 'Billed' },
    'billed': { icon: BsCheckCircleFill, colour: '#28a745', label: 'Billed' },
    'not billed yet': { icon: BsClockFill, colour: '#ffc107', label: 'Not Yet Billed' },
    'not yet billed': { icon: BsClockFill, colour: '#ffc107', label: 'Not Yet Billed' },
    'credit': { icon: BsCreditCardFill, colour: '#17a2b8', label: 'Credit' },
    'credited': { icon: BsCreditCardFill, colour: '#17a2b8', label: 'Credit' },
};

const CostingDetailTable = ({
    data,
    summarySelection: leftSelection,
    detailSelection: rightSelection,
    onRowSelect,
    activeTable,
    dataLoaded
}) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (dataKey) => {
        setSortConfig(prev => ({
            key: dataKey,
            direction: prev.key === dataKey && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const filteredData = useMemo(() => filterDetailRows(data, leftSelection), [data, leftSelection]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (sortConfig.key === 'jobDate' || sortConfig.key === 'weekEnding') {
                const dateA = new Date(aValue);
                const dateB = new Date(bValue);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }

            const strA = String(aValue).toLowerCase();
            const strB = String(bValue).toLowerCase();
            const comparison = strA.localeCompare(strB);
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [filteredData, sortConfig]);

    const totals = useMemo(() => {
        if (!sortedData || sortedData.length === 0) return { hrs: 0, nett: 0 };
        return sortedData.reduce((acc, row) => ({
            hrs: acc.hrs + (parseFloat(row.hrs) || 0),
            nett: acc.nett + (parseFloat(row.nett) || 0)
        }), { hrs: 0, nett: 0 });
    }, [sortedData]);

    const rowGetter = ({ index }) => sortedData[index];

    const headerRenderer = ({ dataKey, label }) => (
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', userSelect: 'none', padding: '1em', height: '35px' }} onClick={() => handleSort(dataKey)}>
            {label}
        </div>
    );

    const handleRowClick = useCallback(({ rowData }) => {
        if (rightSelection && rightSelection.lineId === rowData.lineId) {
            onRowSelect(null);
        } else {
            onRowSelect({
                lineId: rowData.lineId,
                weekEnding: rowData.weekEnding,
                roleName: rowData.roleName,
                siteName: rowData.site_Name,
                jobDay: rowData.jobDay
            });
        }
    }, [rightSelection, onRowSelect]);

    const rowClassName = ({ index }) => {
        if (index < 0) return 'ReactVirtualized__Table__headerRow';
        const row = sortedData[index];
        if (rightSelection && row && rightSelection.lineId === row.lineId) {
            return 'selected-detail-row';
        }
        return '';
    };

    const dateCellRenderer = ({ cellData }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(cellData)}</div>
    );

    const currencyCellRenderer = ({ cellData }) => (
        <div style={{ textAlign: 'right', paddingRight: '8px' }}>{formatCurrencyFull(cellData)}</div>
    );

    const hoursCellRenderer = ({ cellData }) => (
        <div style={{ textAlign: 'right', paddingRight: '8px' }}>{formatHours(cellData)}</div>
    );

    const textCellRenderer = ({ cellData }) => (
        <div
            style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={cellData || ''}
        >
            {cellData || '-'}
        </div>
    );

    const statusCellRenderer = ({ cellData }) => {
        const status = cellData?.trim().toLowerCase();
        const config = STATUS_CONFIG[status];

        if (!config) {
            return <div className="icon-cell" title={cellData || '-'}>{cellData || '-'}</div>;
        }

        const Icon = config.icon;
        return (
            <div className="icon-cell" title={config.label}>
                <span style={{ color: config.colour, fontSize: '1rem' }}>
                    <Icon />
                </span>
            </div>
        );
    };

    const renderers = { date: dateCellRenderer, text: textCellRenderer, hours: hoursCellRenderer, currency: currencyCellRenderer, status: statusCellRenderer };

    const visibleColumns = useMemo(() => {
        if (!data || data.length === 0) return ALL_COLUMNS;
        const firstRow = data[0];
        return ALL_COLUMNS.filter(col => Object.prototype.hasOwnProperty.call(firstRow, col.dataKey));
    }, [data]);

    if (!dataLoaded) {
        return (
            <div className="inline-loading">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="costing-summary-empty">
                <div className="empty-text">No data available</div>
            </div>
        );
    }

    if (!leftSelection) {
        return (
            <div className="empty-state-message">
                <p>Select any row/column/cell on summary table to view details.</p>
            </div>
        );
    }

    if (sortedData.length === 0) {
        return (
            <div className="costing-summary-empty">
                <div className="empty-text">No matching records</div>
            </div>
        );
    }

    return (
        <div className="table-content costing-detail-table">
            <div className="table-wrapper">
                <AutoSizer>
                    {({ height, width }) => (
                        <Table
                            width={width}
                            height={height}
                            headerHeight={TABLE_ROW_HEIGHT}
                            rowHeight={TABLE_ROW_HEIGHT}
                            rowCount={sortedData.length}
                            rowGetter={rowGetter}
                            rowClassName={rowClassName}
                            onRowClick={handleRowClick}
                        >
                            {visibleColumns.map(col => (
                                <Column
                                    key={col.dataKey}
                                    label={col.label}
                                    dataKey={col.dataKey}
                                    width={col.width}
                                    minWidth={col.minWidth}
                                    cellRenderer={renderers[col.rendererType]}
                                    headerRenderer={headerRenderer}
                                    style={col.wrap ? { whiteSpace: 'nowrap', textWrap: 'wrap' } : undefined}
                                />
                            ))}
                        </Table>
                    )}
                </AutoSizer>
            </div>
            <div className="detail-table-footer">
                {visibleColumns.map((col, i) => {
                    if (i === 0) return <div key={col.dataKey} className="footer-cell footer-label">Total</div>;
                    if (col.dataKey === 'hrs') return <div key={col.dataKey} className="footer-cell footer-hours">{formatHours(totals.hrs)}</div>;
                    if (col.dataKey === 'nett') return <div key={col.dataKey} className="footer-cell footer-nett">{formatCurrencyFull(totals.nett)}</div>;
                    return <div key={col.dataKey} className="footer-cell"></div>;
                })}
            </div>
        </div>
    );
};

CostingDetailTable.propTypes = {
    data: PropTypes.array,
    summarySelection: PropTypes.object,
    detailSelection: PropTypes.object,
    onRowSelect: PropTypes.func.isRequired,
    activeTable: PropTypes.string,
    dataLoaded: PropTypes.bool
};

export default CostingDetailTable;
